<template>
  <v-card
    class="pa-0"
    :width="isMobile ? '100%' : 485"
    style="background-color: #818181; color: white; position: absolute; cursor: default"
    @mousedown="startDrag"
    :style="positionStyling"
  >
    <v-card-title style="background-color: #818181; color: white" class="pa-4">
      <v-row justify="space-between" style="min-width: 0">
        <v-col
          class="text-body-1 text-md-h6 flex-grow-1"
          :style="{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: '8', // or '3' for three lines
            overflow: 'hidden',
            whiteSpace: 'normal',
            lineHeight: '1.25',
            minWidth: '0'
          }"
        >
          {{ truncatedTitle }}
        </v-col>

        <v-col class="flex-grow-0">
          <v-btn icon variant="outlined" size="x-small" @click="$emit('close')">
            <v-icon size="large" :icon="mdiWindowClose"></v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text>
      <v-row justify="end" align="center">
        <v-col class="text-body-2"
          >Sample ID: {{ metadataObject?.gendib_sample_id || 'N/A' }}
        </v-col>

        <v-col class="flex-grow-0 py-0 px-3">
          <v-tooltip location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                icon
                :disabled="isSensitiveData || !metadataObject.article_doi"
                :href="`${metadataObject?.article_link}`"
                target="_blank"
                size="small"
              >
                <v-icon
                  size="large"
                  :icon="mdiBookOpenVariantOutline"
                  :color="metadataObject.article_doi ? '#D4A6FF' : 'grey'"
                >
                </v-icon>
              </v-btn>
            </template>

            Click to open the published paper
          </v-tooltip>
        </v-col>

        <v-col class="flex-grow-0">
          <v-tooltip location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                icon
                :disabled="isSensitiveData || !metadataObject.data_doi"
                :href="`${metadataObject?.data_link}`"
                target="_blank"
                size="small"
              >
                <v-icon
                  size="large"
                  :icon="mdiDatabaseArrowDown"
                  :color="metadataObject.data_doi ? '#D4A6FF' : 'grey'"
                >
                </v-icon>
              </v-btn>
            </template>

            Click to open the published data
          </v-tooltip>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col class="py-0">Sample Size: {{ sampleSize }}</v-col>
      </v-row>

      <v-row v-if="isSensitiveData">
        <v-col cols="6">
          <v-row>
            <v-col class="flex-grow-0">
              <v-icon size="large" :icon="mdiShieldKey" />
            </v-col>
            <v-col class="flex-shrink-1"> Sensitive Data </v-col>
          </v-row>
        </v-col>

        <v-col cols="6" class="text-caption flex-grow-1">
          {{ sensitiveDataDisclaimer }}
        </v-col>
      </v-row>

    </v-card-text>

    <v-card-text>
      <!-- popup body with two column layout -->
      <v-row no-gutters>
        <v-col cols="6" class="pr-2">
          <v-row class="text-h6 pb-2" no-gutters style="color: white"> Contacts </v-row>

          <v-row no-gutters>
            <v-col cols="12" style="max-height: 350px; overflow: auto; scrollbar-width: thin">
              <v-chip-group column>
                <template v-for="(author, index) in formattedAuthors" :key="index">
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-chip
                        v-bind="props"
                        class="my-1 author-chip"
                        outlined
                        :size="authorList.length > 10 ? 'small' : 'default'"
                        style="
                          max-width: 100%;
                          white-space: nowrap;
                          overflow: hidden;
                          text-overflow: ellipsis;
                          display: flex;
                          align-items: center;
                        "
                      >
                        <!-- Render name with link if ORCID exists -->
                        <template v-if="author?.orcid">
                          <a
                            :href="author.orcid"
                            target="_blank"
                            class="author-link"
                            style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis"
                          >
                            {{ author.name }}
                            <v-icon
                              :icon="mdiAccountCircleOutline"
                              size="18"
                              color="green"
                              class="ms-1"
                            >
                            </v-icon>
                          </a>
                        </template>
                        <template v-else>
                          <span
                            style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis"
                            >{{ author?.name }}</span
                          >
                        </template>
                      </v-chip>
                    </template>

                    <!-- Tooltip content: Show "First Author" or "Last Author" -->
                    <span v-if="index === 0">First Author</span>
                    <span v-else-if="index === formattedAuthors.length - 1">Last Author</span>
                  </v-tooltip>
                </template>
              </v-chip-group>
            </v-col>
          </v-row>

          <v-row class="text-h6 mt-4 pb-2" no-gutters style="color: white"> Markers </v-row>

          <v-row no-gutters>
            <v-col>
              {{ metadataObject?.marker_types || 'N/A' }}
            </v-col>
          </v-row>
        </v-col>

        <!-- Species Section -->
        <v-col cols="6" class="pl-2">
          <v-row class="text-h6 pb-2" no-gutters style="color: white"> Species group </v-row>

          <v-row no-gutters>
            <v-col>
              <v-chip
                variant="elevated"
                :style="{ backgroundColor: speciesChipColor, color: 'white', fontWeight: 'bold' }"
              >
                {{ metadataObject?.species_group || 'N/A' }}
              </v-chip>
            </v-col>
          </v-row>

          <v-row class="text-h6 mt-4 pb-2" no-gutters style="color: white"> Species </v-row>

          <v-row no-gutters>
            <v-col class="font-italic">
              {{ metadataObject?.species_scientific || 'N/A' }}
            </v-col>
          </v-row>

          <v-row class="mt-4" no-gutters>
            <v-col cols="12">
              <!-- Tooltip for GBIF image -->
              <v-tooltip location="bottom">
                <template v-slot:activator="{ props }">
                  <a v-if="speciesPhoto && speciesLink" :href="speciesLink" target="_blank">
                    <v-img
                      v-bind="props"
                      :src="speciesPhoto"
                      alt="Species photo"
                      height="150"
                      class="species-photo"
                    />
                  </a>
                </template>
                <span>Click to view on GBIF</span>
              </v-tooltip>

              <!-- Show fallback MD icon if no species photo -->
              <div v-if="!speciesPhoto" class="text-center species-photo-fallback">
                <v-icon size="48" color="grey" :icon="mdiImageOff"></v-icon>
                <p class="photo-label">Image not available</p>
              </div>
            </v-col>

            <v-col v-if="speciesPhoto" cols="12" class="photo-label">
              Retrieved from GBIF.org
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent } from 'vue'
import axios from 'axios'

import {
  mdiAccountCircleOutline,
  mdiBookOpenVariantOutline,
  mdiDatabaseArrowDown,
  mdiImageOff,
  mdiNewspaper,
  mdiShieldKey,
  mdiWindowClose
} from '@mdi/js'

import { useDisplay } from 'vuetify'

export interface Author {
  name: string
  orcid: string | null
}


export default defineComponent({
  props: {
    metadataObject: {
      type: Object as PropType<{ [key: string]: any }>,
      default: () => ({})
    },
    width: {
      type: Number, // Define width as a number prop
      default: 400 // Default width
    }
  },
  emits: ['close'],
  data: () => ({
    speciesPhoto: '', // URL for the species photo
    speciesLink: null as string | null, // GBIF occurrence platform link
    authorList: [] as Author[],
    mdiWindowClose,
    mdiNewspaper,
    mdiDatabaseArrowDown,
    mdiAccountCircleOutline,
    mdiBookOpenVariantOutline,
    mdiImageOff,
    mdiShieldKey,
    position: { x: 25, y: 175 }, // Initial position of the popup
    isDragging: false,
    startPosition: { x: 0, y: 0 },
    sensitiveDataDisclaimer:
      'This is a sensitive species. If you need to obtain data on this species, please contact us at gendib@wsl.ch and indicate the reason for your request.'
  }),
  computed: {
    isMobile() {
      const display = useDisplay();
      return display.smAndDown.value;
    },
    positionStyling() {
      if (this.isMobile) {
        return { top: '5px', left: 0, 'z-index': 500 }
      }

      return { top: this.position.y + 'px', left: this.position.x + 'px' }
    },
    isSensitiveData() {
      return this.metadataObject?.sensitive_species || false
    },
    sampleSize() {
      return this.metadataObject?.sample_size || 1
    },
    popupTitle(): string {
      return this.metadataObject?.publication_name || ''
    },
    truncatedTitle(): string {
      const limit = 300 // Adjust the limit as needed
      if (!this.popupTitle) return ''
      return this.popupTitle.length > limit
        ? this.popupTitle.slice(0, limit) + '...'
        : this.popupTitle
    },
    formattedAuthors() {
      if (this.authorList.length <= 2) {
        return this.authorList
      }

      // Take only the first and last author
      return [this.authorList[0], this.authorList[this.authorList.length - 1]]
    },

    speciesChipColor() {
      const speciesGroup = this.metadataObject?.species_group?.toLowerCase()
      switch (speciesGroup) {
        case 'plant':
          return '#537A29'
        case 'protista':
          return '#219EBC'
        case 'vertebrate':
          return '#FFBF1F'
        case 'invertebrate':
          return '#C1121F'
        case 'fungi':
          return '#023047'
        default:
          return '#FFFFFF' // Default color (white)
      }
    }
  },
  watch: {
    metadataObject: {
      immediate: true,
      handler(newVal) {
        if (newVal?.species_scientific) {
          this.fetchSpeciesPhoto(newVal.species_scientific)
        }

        // Handle authors even if `author_orcid_list` is missing
        if (newVal?.author_list) {
          const orcidList = newVal?.author_orcid_list || '' // Default to an empty string if `author_orcid_list` is missing
          this.processAuthorList(newVal.author_list, orcidList)
        } else {
          // Handle case where no author list exists
          console.warn('No authors provided in metadataObject.')
          this.authorList = [{ name: 'Unknown Author', orcid: null }]
        }
      }
    }
  },
  methods: {
    // Preprocessing function to clean up species names
    preprocessSpeciesName(speciesName: string): string | undefined {
      let cleanedName = speciesName?.split(/[;/]/)[0]?.trim()
      const words = cleanedName?.split(' ')

      if (words && words?.length > 2) {
        cleanedName = words?.slice(0, 2).join(' ')
      }

      return cleanedName
    },
    async fetchSpeciesPhoto(speciesName: string): Promise<void> {
      const speciesSearchUrl = 'https://api.gbif.org/v1/species/search'
      const occurrenceSearchUrl = 'https://api.gbif.org/v1/occurrence/search'

      try {
        const cleanedName = this.preprocessSpeciesName(speciesName)

        const { data: speciesData } = await axios.get(speciesSearchUrl, {
          params: {
            q: cleanedName,
            rank: 'species',
            exact: true
          }
        })

        let nubKey = speciesData.results.find((result: any) => result.nubKey)?.nubKey

        if (!nubKey) {
          const { data: retryData } = await axios.get(speciesSearchUrl, {
            params: {
              q: cleanedName,
              rank: 'species',
              exact: false
            }
          })
          nubKey = retryData.results.find((result: any) => result.nubKey)?.nubKey
        }

        if (!nubKey) {
          console.warn('No nubKey found for the species.')
          this.speciesPhoto = ''
          this.speciesLink = null // No link if no nubKey
          return
        }

        // Set the GBIF link using the nubKey
        this.speciesLink = `https://www.gbif.org/occurrence/gallery?media_type=StillImage&taxon_key=${nubKey}`

        const { data: occurrenceData } = await axios.get(occurrenceSearchUrl, {
          params: {
            taxonKey: nubKey,
            mediaType: 'StillImage',
            limit: 1
          }
        })

        if (occurrenceData.results.length > 0) {
          const media = occurrenceData.results[0].media || []
          if (media.length > 0) {
            this.speciesPhoto = media[0].identifier
            return
          }
        }

        this.speciesPhoto = ''
      } catch (error) {
        console.error('Error fetching species photo:', error)
        this.speciesPhoto = ''
        this.speciesLink = null
      }
    },
    processAuthorList(authorList: string, orcidList: string) {
      const authors = authorList.split(';').map((name) => name.trim())
      const orcids = orcidList ? orcidList.split(';').map((orcid) => orcid.trim()) : []

      this.authorList = authors.map((name, index) => ({
        name: name || 'Unknown Author', // Fallback for empty names
        orcid: orcids[index] && orcids[index] !== '' ? orcids[index] : null // Null if no ORCID
      }))
    },
    // drag functionality
    startDrag(event: MouseEvent) {
      this.isDragging = true
      this.startPosition.x = event.clientX - this.position.x
      this.startPosition.y = event.clientY - this.position.y

      document.addEventListener('mousemove', this.drag)
      document.addEventListener('mouseup', this.stopDrag)
    },
    drag(event: MouseEvent) {
      if (!this.isDragging) return

      this.position.x = event.clientX - this.startPosition.x
      this.position.y = event.clientY - this.startPosition.y

      // Ensure the popup stays within the viewport
      const maxX = window.innerWidth - 445 // Width of the popup
      const maxY = window.innerHeight - 100 // Avoid bottom overflow

      this.position.x = Math.max(0, Math.min(this.position.x, maxX))
      this.position.y = Math.max(0, Math.min(this.position.y, maxY))
    },
    stopDrag() {
      this.isDragging = false
      document.removeEventListener('mousemove', this.drag)
      document.removeEventListener('mouseup', this.stopDrag)
    }
  }
})
</script>

<style scoped>
.metadata-container {
  /*
  max-width: 500px; !* Adjust to the desired width *!
  */
  margin: 20px auto; /* Center the container with margin at the top and bottom */
  padding: 16px;
  border-radius: 8px; /* Rounded corners for aesthetics */
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); /* Soft shadow for depth */
  background: #f5f5f5; /* Light grey background for the container */
  overflow: hidden; /* Prevents background from spilling outside the border-radius */
}

.author-link {
  text-decoration: none;
  color: inherit;
}

.photo-label {
  font-size: 14px;
  text-align: center;
  color: #dddddd;
  margin-top: 8px;
}
@media (max-width: 600px) {
  .metadata-container {
    max-width: 90%;
    padding: 8px;
  }
}
</style>
